/**
 * 年齢認証レコード（`zp.age`）の読み書き（FR-001 / FR-003、`05-database-schema.md` §4）。
 *
 * ストレージと現在時刻を引数で受け取る純ロジックであり、DOM に依存しない。
 * `localStorage` の実体を渡すのは `age-gate.ts` で、テストは差し替えたストレージで
 * 規則そのものを検証する。
 *
 * 取り扱い規則（`05` §4.2）:
 * - 有効期限は `at` から 90 日。超過は未認証として扱う
 * - 破損（JSON パース失敗・`v` 不一致・型不整合）はレコードを削除し、未認証として扱う
 * - ストレージ操作の失敗は呼び出し側へ伝播させない（エラーは表示しない）
 */

/** `localStorage` 互換の最小インターフェース。 */
export interface AgeStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** レコードのキー。 */
export const AGE_KEY = 'zp.age';

/** レコードバージョン。不一致は破棄する（前方・後方互換を取らない。`05` §7）。 */
export const AGE_RECORD_VERSION = 1;

/** 有効期限: 90 日（FR-001）。 */
export const AGE_TTL_MS = 90 * 24 * 60 * 60 * 1000;

/**
 * 認証済みとして扱えるかを返す。
 *
 * 期限切れ・破損のレコードはこの場で削除する（残しても次回また同じ判定になるだけで、
 * 破損レコードを持ち続ける理由がない）。
 */
export function readVerified(storage: AgeStorage, now: number): boolean {
  let raw: string | null;
  try {
    raw = storage.getItem(AGE_KEY);
  } catch {
    return false;
  }
  if (raw === null) return false;

  const record = parseRecord(raw);
  if (record === null || now - record.at >= AGE_TTL_MS) {
    clearAge(storage);
    return false;
  }
  return record.ok;
}

/** 認証済みレコードを保存する。保存できなくても失敗を伝播させない。 */
export function writeVerified(storage: AgeStorage, now: number): void {
  try {
    storage.setItem(AGE_KEY, JSON.stringify({ v: AGE_RECORD_VERSION, ok: true, at: now }));
  } catch {
    /* 保存できない環境ではメモリフォールバック（age-gate.ts）が受け持つ */
  }
}

/** レコードを破棄する（`?reset-age`、FR-003）。 */
export function clearAge(storage: AgeStorage): void {
  try {
    storage.removeItem(AGE_KEY);
  } catch {
    /* 破棄すら失敗する環境では、読み取りも失敗して未認証になるため実害がない */
  }
}

/** JSON を検証つきで読む。構造が仕様と一致しないものはすべて `null`（= 破損）。 */
function parseRecord(raw: string): { ok: boolean; at: number } | null {
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof value !== 'object' || value === null) return null;
  const record = value as Record<string, unknown>;
  if (record.v !== AGE_RECORD_VERSION) return null;
  if (typeof record.ok !== 'boolean') return null;
  if (typeof record.at !== 'number' || !Number.isFinite(record.at)) return null;
  return { ok: record.ok, at: record.at };
}
